// angular
import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
// components
import { PaginationComponent } from '@app/core/components/pagination/pagination.component';
import { TenderCardComponent } from '@app/core/components/tender-card/tender-card.component';
import { NoContentComponent } from '@app/core/components/no-content/no-content.component';
import { TenderCardSkeletonComponent } from '@app/core/components/tender-card/tender-card-skeleton/tender-card-skeleton.component';
import { SortsBarComponent } from '@app/core/components/sorts-bar/sorts-bar.component';
import { FiltersPanelComponent } from '@app/core/components/filters-panel/filters-panel.component';
import { ProductsFiltersComponent } from '@app/+products/products-list/components/products-filters/products-filters.component';
import { ProductsFiltersDialogComponent } from '@app/+products/products-list/dialogs/products-filters-dialog/products-filters-dialog.component';
// services
import { TendersService } from '../shared/services/tenders.service';
import { DialogService } from '@app/core/shared/services/dialog.service';
// interfaces & enums
import { ITender } from '../shared/interfaces/tender.interface';
import { TenderStatesEnum } from '../shared/enums/tender-states.enum';

@Component({
  selector: 'pwj-tenders-list',
  standalone: true,
  imports: [
    TenderCardComponent,
    PaginationComponent,
    TranslateModule,
    SortsBarComponent,
    NgxSkeletonLoaderModule,
    NoContentComponent,
    TenderCardSkeletonComponent,
    FiltersPanelComponent,
    ProductsFiltersComponent,
  ],
  templateUrl: './tenders-list.component.html',
  styleUrl: './tenders-list.component.scss',
})
export class TendersListComponent {
  public filters: string[] = Object.values(TenderStatesEnum);
  public tendersData: ITender[] = [];
  public allTendersCount!: number;
  public isLoading = true;
  public currentTab?: string;

  readonly filtersSheetOpen = signal(false);
  readonly showFiltersTriggerOnSortBar = signal(true);

  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _tendersService = inject(TendersService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _dialogService = inject(DialogService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _platformId = inject(PLATFORM_ID);

  constructor() {
    this._activatedRoute.queryParams.subscribe(params => {
      this.currentTab = params['filter_status'];

      const take = params['perPage'] || 6;
      const skip = params['page'] ? (Number(params['page']) - 1) * take : 0;

      let selectedParams: Params = {
        ...params,
        take,
        skip,
        filter_status: this.currentTab ? [this.currentTab] : undefined,
        page: null,
        perPage: null,
      };

      selectedParams = Object.fromEntries(
        Object.entries(selectedParams).filter(
          ([_, value]) => value != null && value !== ''
        )
      );

      this.fetchData(selectedParams);
    });

    effect(() => {
      this.isLoading = this._tendersService.isTendersListLoading();
      this._cdr.detectChanges();
    });

    effect(() => {
      this.tendersData = this._tendersService.tendersList();

      if (this._tendersService?.allTendersCount() !== null) {
        this.allTendersCount =
          this._tendersService?.allTendersCount() as number;
      }

      this._cdr.detectChanges();
    });

    afterNextRender(() => {
      if (!isPlatformBrowser(this._platformId)) {
        return;
      }
      const mq = window.matchMedia('(max-width: 65.99em)');
      const sync = () => this.showFiltersTriggerOnSortBar.set(mq.matches);
      sync();
      mq.addEventListener('change', sync);
      this._destroyRef.onDestroy(() => mq.removeEventListener('change', sync));
    });
  }

  public fetchData(params: Params) {
    this._tendersService.getAllTenders(params);
  }

  onFiltersFromSortBar(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }
    if (window.matchMedia('(min-width: 48em)').matches) {
      this.openFiltersDialog();
    } else {
      this.openFiltersSheet();
    }
  }

  openFiltersSheet(): void {
    this.filtersSheetOpen.set(true);
  }

  closeFiltersSheet(): void {
    this.filtersSheetOpen.set(false);
  }

  openFiltersDialog(): void {
    this._dialogService.open(ProductsFiltersDialogComponent, {
      width: 'min(23.5rem, calc(100vw - 1.25rem))',
      maxWidth: 'calc(100vw - 1.25rem)',
      maxHeight: 'min(90vh, 44rem)',
      height: 'auto',
      panelClass: 'overlay-panel--filters-dialog',
    });
  }
}
